/* eslint-disable jsx-a11y/label-has-associated-control */
/* eslint-disable consistent-return */
import {
  ArrowPathIcon,
  PhoneIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';

import {
  useCancelPoliceOutreachMutation,
  useGetCommandCenterDataQuery,
  useGetCommandCenterStatsQuery,
  useTerminateEnhancedBlandCallWorkflowMutation,
  useTriggerMassPoliceSchedulerMutation,
  useTriggerPoliceOutreachMutation,
  useTriggerPoliceSchedulerMutation,
} from '@/services/api/blandService';
import { MatterVoiceAgentSummary } from '@/types/enhanced-bland-call';

import { TaskDetail } from '../tasks/task-detail/TaskDetail';
import { BulkActions } from './BulkActions';
import { CommandCenterFilters } from './CommandCenterFilters';
import { CommandCenterStats } from './CommandCenterStats';
import { MatterList } from './MatterList';
import { QuickFilters } from './QuickFilters';

// Hooks for state management
const useCommandCenterState = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [showActiveOnly, setShowActiveOnly] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [quickFilters, setQuickFilters] = useState<Set<string>>(new Set());
  const [selectedMatters, setSelectedMatters] = useState<Set<string>>(
    new Set(),
  );
  const [expandedMatters, setExpandedMatters] = useState<Set<string>>(
    new Set(),
  );
  const [autoRefresh, setAutoRefresh] = useState(true);

  // Quick filter toggle
  const toggleQuickFilter = useCallback((filter: string) => {
    setQuickFilters((prev) => {
      const newFilters = new Set(prev);
      if (newFilters.has(filter)) {
        newFilters.delete(filter);
      } else {
        newFilters.add(filter);
      }
      return newFilters;
    });
  }, []);

  // Selection management
  const toggleMatterSelection = useCallback((matterId: string) => {
    setSelectedMatters((prev) => {
      const newSelection = new Set(prev);
      if (newSelection.has(matterId)) {
        newSelection.delete(matterId);
      } else {
        newSelection.add(matterId);
      }
      return newSelection;
    });
  }, []);

  const selectAllMatters = useCallback((matters: MatterVoiceAgentSummary[]) => {
    setSelectedMatters(new Set(matters.map((m) => m.matter_id)));
  }, []);

  const clearSelection = useCallback(() => {
    setSelectedMatters(new Set());
  }, []);

  // Expansion management
  const toggleMatterExpansion = useCallback((matterId: string) => {
    setExpandedMatters((prev) => {
      const newExpanded = new Set(prev);
      if (newExpanded.has(matterId)) {
        newExpanded.delete(matterId);
      } else {
        newExpanded.add(matterId);
      }
      return newExpanded;
    });
  }, []);

  return {
    // Search and filters
    searchTerm,
    setSearchTerm,
    statusFilter,
    setStatusFilter,
    showActiveOnly,
    setShowActiveOnly,
    quickFilters,
    toggleQuickFilter,

    // Pagination
    page,
    setPage,
    pageSize,
    setPageSize: (size: number) => {
      setPageSize(size);
      setPage(1); // Reset to first page
    },

    // Selection
    selectedMatters,
    toggleMatterSelection,
    selectAllMatters,
    clearSelection,

    // Expansion
    expandedMatters,
    toggleMatterExpansion,

    // Auto-refresh
    autoRefresh,
    setAutoRefresh,
  };
};

export const BlandCommandCenter: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const taskId = searchParams.get('task_id');

  const state = useCommandCenterState();

  // API mutations
  const [triggerMassScheduler] = useTriggerMassPoliceSchedulerMutation();
  const [cancelOutreach] = useCancelPoliceOutreachMutation();
  const [triggerOutreach] = useTriggerPoliceOutreachMutation();
  const [terminateWorkflow] = useTerminateEnhancedBlandCallWorkflowMutation();
  const [triggerScheduler] = useTriggerPoliceSchedulerMutation();

  // Build query parameters
  const queryParams = useMemo(() => {
    const params: Record<string, string | number | boolean> = {
      page: state.page,
      page_size: state.pageSize,
    };

    if (state.searchTerm) params.search = state.searchTerm;
    if (state.statusFilter) params.status = state.statusFilter;
    if (state.showActiveOnly) params.active_only = true;

    // Apply quick filters
    if (state.quickFilters.has('active')) params.active_only = true;
    if (state.quickFilters.has('scheduled')) params.status = 'scheduled';
    if (state.quickFilters.has('failed')) params.status = 'failed';
    if (state.quickFilters.has('cancelled')) params.status = 'cancelled';

    return params;
  }, [
    state.searchTerm,
    state.statusFilter,
    state.showActiveOnly,
    state.page,
    state.pageSize,
    state.quickFilters,
  ]);

  // Data fetching
  const {
    data: commandCenterData,
    isLoading,
    error,
    refetch,
  } = useGetCommandCenterDataQuery(queryParams);

  const { data: statsData } = useGetCommandCenterStatsQuery();

  // Auto-refresh effect
  useEffect(() => {
    if (!state.autoRefresh) return;

    const interval = setInterval(() => {
      refetch();
    }, 10000); // Refresh every 10 seconds

    return () => clearInterval(interval);
  }, [state.autoRefresh, refetch]);

  // Bulk action handler
  const handleBulkAction = useCallback(
    async (action: string, items: string[]) => {
      switch (action) {
        case 'trigger_scheduler':
          await Promise.all(
            items.map((matterId) => triggerScheduler({ matterId }).unwrap()),
          );
          break;
        case 'bulk_cancel': {
          // Cancel all scheduled runs for the selected matters
          const cancelPromises: Promise<any>[] = [];

          // Find all scheduled runs in the selected matters
          if (commandCenterData) {
            commandCenterData.matters.forEach((matter) => {
              if (items.includes(matter.matter_id) && matter.tasks_data) {
                // Look through all tasks in this matter
                Object.values(matter.tasks_data).forEach((taskData: any) => {
                  if (taskData.runs) {
                    // Find scheduled runs and add them to cancel queue
                    taskData.runs.forEach((run: any) => {
                      if (run.status === 'scheduled') {
                        cancelPromises.push(
                          cancelOutreach({
                            run_id: run.id,
                            reason: 'Bulk cancelled by user',
                            matterId: matter.matter_id,
                          }).unwrap(),
                        );
                      }
                    });
                  }
                });
              }
            });
          }

          if (cancelPromises.length === 0) {
            toast.info('No scheduled runs found in selected matters');
            return;
          }

          await Promise.all(cancelPromises);
          // Don't show additional success message here since BulkActions will show one
          break;
        }
        case 'export':
          // TODO: Implement export functionality
          toast.info('Export functionality coming soon');
          break;
        default:
          throw new Error(`Unknown bulk action: ${action}`);
      }
    },
    [triggerScheduler, cancelOutreach, commandCenterData],
  );

  // Task selection handler
  const handleTaskSelect = useCallback(
    (selectedTaskId: string) => {
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.set('task_id', selectedTaskId);
      setSearchParams(newSearchParams);
    },
    [searchParams, setSearchParams],
  );

  const handleBackToMatters = useCallback(() => {
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.delete('task_id');
    setSearchParams(newSearchParams);
  }, [searchParams, setSearchParams]);

  // Find selected task's matter
  const selectedTaskMatterId = useMemo(() => {
    if (!taskId || !commandCenterData) return null;

    const foundMatter = commandCenterData.matters.find(
      (matter) =>
        matter.tasks_data &&
        Object.values(matter.tasks_data).some((taskData: any) =>
          taskData.runs.some((run: any) => run.task_id === taskId),
        ),
    );

    return foundMatter?.matter_id || null;
  }, [taskId, commandCenterData]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Global shortcuts
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        document
          .querySelector<HTMLInputElement>('input[placeholder*="Search"]')
          ?.focus();
      }

      if ((e.metaKey || e.ctrlKey) && e.key === 'r') {
        e.preventDefault();
        refetch();
      }

      if (e.key === 'Escape') {
        state.clearSelection();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [refetch, state.clearSelection]);

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <XMarkIcon className="h-12 w-12 text-red-400 mx-auto mb-4" />
          <h2 className="text-lg font-semibold text-gray-900 mb-2">
            Connection Error
          </h2>
          <p className="text-gray-600 mb-4">
            Unable to load command center data.
          </p>
          <button
            type="button"
            onClick={() => refetch()}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  const matters = commandCenterData?.matters || [];
  const totalPages = commandCenterData?.total_pages || 1;

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Main Content */}
      <div className={`flex-1 ${taskId ? 'max-w-[calc(100%-400px)]' : ''}`}>
        {/* Header */}
        <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
          <div className="px-4 py-3 border-b border-gray-100">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <h1 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                  <PhoneIcon className="h-5 w-5 text-blue-600" />
                  Voice Agent Command Center
                </h1>

                {/* Auto-refresh toggle */}
                <label className="flex items-center gap-2 text-xs">
                  <input
                    type="checkbox"
                    checked={state.autoRefresh}
                    onChange={(e) => state.setAutoRefresh(e.target.checked)}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  Auto-refresh (10s)
                </label>
              </div>

              <button
                type="button"
                onClick={() => refetch()}
                disabled={isLoading}
                className="flex items-center gap-1 px-2 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
              >
                <ArrowPathIcon
                  className={`h-3 w-3 ${isLoading ? 'animate-spin' : ''}`}
                />
                Refresh
              </button>
            </div>
          </div>

          {/* Stats Bar */}
          {statsData && (
            <CommandCenterStats
              stats={statsData}
              onTriggerMassScheduler={async () => {
                try {
                  await triggerMassScheduler().unwrap();
                  toast.success('Mass police scheduler triggered successfully');
                } catch {
                  toast.error('Failed to trigger mass police scheduler');
                }
              }}
            />
          )}

          {/* Quick Filters */}
          <QuickFilters
            activeFilters={state.quickFilters}
            onFilterToggle={state.toggleQuickFilter}
            stats={statsData}
          />

          {/* Main Filters */}
          <CommandCenterFilters
            searchTerm={state.searchTerm}
            onSearchChange={state.setSearchTerm}
            statusFilter={state.statusFilter}
            onStatusFilterChange={state.setStatusFilter}
            showActiveOnly={state.showActiveOnly}
            onActiveOnlyChange={state.setShowActiveOnly}
            page={state.page}
            totalPages={totalPages}
            onPageChange={state.setPage}
            pageSize={state.pageSize}
            onPageSizeChange={state.setPageSize}
            selectedCount={state.selectedMatters.size}
            totalCount={matters.length}
            onSelectAll={() => state.selectAllMatters(matters)}
            onClearSelection={state.clearSelection}
          />

          {/* Bulk Actions */}
          <BulkActions
            selectedItems={state.selectedMatters}
            selectedType="matters"
            onClearSelection={state.clearSelection}
            onBulkAction={handleBulkAction}
            isLoading={isLoading}
          />
        </div>

        {/* Content */}
        <div className="p-3">
          {isLoading ? (
            <div className="text-center py-16">
              <div className="inline-block w-6 h-6 border-2 border-gray-300 border-t-blue-600 rounded-full animate-spin mb-3" />
              <p className="text-sm text-gray-600">Loading...</p>
            </div>
          ) : (
            <MatterList
              matters={matters}
              selectedMatters={state.selectedMatters}
              expandedMatters={state.expandedMatters}
              onToggleSelection={state.toggleMatterSelection}
              onToggleExpansion={state.toggleMatterExpansion}
              onTaskSelect={handleTaskSelect}
              totalMatters={commandCenterData?.total_matters || 0}
              currentPage={state.page}
              totalPages={totalPages}
              hasFilters={
                !!(
                  state.searchTerm ||
                  state.statusFilter ||
                  state.showActiveOnly ||
                  state.quickFilters.size
                )
              }
            />
          )}
        </div>
      </div>

      {/* Task Sidebar */}
      {taskId && selectedTaskMatterId && (
        <div className="w-[400px] bg-white border-l border-gray-200 flex flex-col">
          <div className="flex items-center p-3 border-b border-gray-200 bg-gray-50">
            <button
              type="button"
              onClick={handleBackToMatters}
              className="flex items-center gap-1 text-xs text-gray-600 hover:text-gray-900"
            >
              ← Back to matters
            </button>
          </div>
          <div className="flex-1 overflow-y-auto">
            <TaskDetail
              taskId={taskId}
              matterId={selectedTaskMatterId}
              onClose={handleBackToMatters}
              isModal={false}
            />
          </div>
        </div>
      )}
    </div>
  );
};
